import React from "react";
import { usd } from "../../lib/api";
import { Shield, Shirt, Award, MapPin, CalendarDays, CheckCircle2, Dumbbell, Hand, ClipboardList, Trophy, UtensilsCrossed, GraduationCap, Heart, Clock, Mail, MessageCircle } from "lucide-react";

const IMG = "https://static.prod-images.emergentagent.com/jobs/42825654-4575-4692-be4e-a545ce1c0ddc/images/";
const HERO_IMG = `${IMG}034989b0e8f2f642f5b38fe6b1d5ed4a71d866196e366c6df25629e1a73ab428.jpeg`;
const TRAIN_IMG = `${IMG}195eb000683c112ad26bb44fb9223d68049cd9f50caad382edfd0f5a4c56e949.jpeg`;
const GK_IMG = `${IMG}ffa47324ecd66514aef2f3345890fc37dd93be534ba6e7d7c427cb472f8549cd.jpeg`;
const COACH_IMG = `${IMG}6bd8f239e6a01f534e27c5554ca9e9328797d5b0a187f095f46ad17981635956.jpeg`;
const JERSEY_IMG = "https://customer-assets-eiarnc6j.emergentagent.net/job_camp-checkout-1/artifacts/lijx5dc7_ChatGPT%20Image%2020_09_2026%2C%2021_47_20.png";
const WRAP = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

const BENEFITS = [
  { icon: CalendarDays, title: "5-Day Camp", desc: "Full participation in all camp activities, June 28 – July 2." },
  { icon: Award, title: "Official Scarf", desc: "One official Sporting CP scarf." },
  { icon: Shield, title: "Accident Insurance", desc: "Sports accident insurance included." },
  { icon: Shirt, title: "Training Kit", desc: "Training T-shirt, shorts, and socks." },
];

const SERVICES = [
  { icon: Dumbbell, name: "Individual Soccer Training", price: "from $100", desc: "40-min sessions. Single, daily package ($350), or double daily ($500).", testid: "svc-football" },
  { icon: Hand, name: "Individual Goalkeeper Training", price: "from $100", desc: "40-min sessions. Single, daily package ($350), or double daily ($500).", testid: "svc-goalkeeper" },
  { icon: ClipboardList, name: "Personalized Feedback", price: "from $50", desc: "Feedback with written report. Single or 5-day package ($150).", testid: "svc-feedback" },
  { icon: Trophy, name: "Complete Development Package", price: "$250", desc: "5 feedback sessions + written report + video analysis + development plan.", premium: true, testid: "svc-development" },
  { icon: UtensilsCrossed, name: "Meal Service", price: "$30/day", desc: "Balanced meals with coaches. Requires a training booking that day. 5-day: $150.", testid: "svc-meal" },
];

const TIER_BADGE = { active: ["bg-[#008057] text-white", "Active"], upcoming: ["bg-slate-200 text-slate-600", "Upcoming"], expired: ["bg-slate-200 text-slate-500", "Expired"] };

export function HeroSection({ camp, closed, onRegister }) {
  return (
    <section className="relative">
      <img src={HERO_IMG} alt="Youth soccer camp" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 hero-overlay" />
      <div className={`relative ${WRAP} py-24 md:py-36`}>
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-white text-xs font-semibold uppercase tracking-widest backdrop-blur">
            <MapPin size={14} /> Brunner & Lay Soccer Complex, Springdale
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white mt-5 leading-none tracking-tight">
            SPORTING CP CAMP<br /><span className="text-[#F3C242]">ARKANSAS 2027</span>
          </h1>
          <p className="text-white/85 text-lg mt-5 max-w-xl">{camp?.intro || "Five unforgettable days of soccer for boys and girls aged 6-17."}</p>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F3C242]/15 border border-[#F3C242]/40 px-4 py-2 text-[#F3C242] text-sm font-semibold mt-5">
            <GraduationCap size={18} /> Coached by Sporting Academy professionals
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <button data-testid="hero-register-cta" onClick={onRegister} disabled={closed} className="rounded-full bg-[#F3C242] hover:brightness-105 disabled:opacity-60 text-[#03281B] px-8 py-3.5 font-bold text-lg transition-all shadow-xl gold-glow">
              {closed ? "Registration Closed" : "Register Participant Now"}
            </button>
            <div data-testid="hero-schedule" className="flex items-center gap-2 text-white/80 text-sm font-semibold">
              <CalendarDays size={18} /> June 28 – July 2, 2027 &middot; {camp?.schedule?.camp_hours || "9:00 AM – 1:00 PM daily"} &middot; Ages 6-17
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CoachesSection() {
  return (
    <section className={`${WRAP} pt-16 md:pt-24`}>
      <div className="rounded-2xl bg-[#03281B] overflow-hidden grid md:grid-cols-2">
        <img src={COACH_IMG} alt="Sporting CP Academy coaching session" className="w-full h-full object-cover aspect-video md:aspect-auto" />
        <div className="p-8 md:p-12">
          <div className="inline-flex items-center gap-2 text-[#F3C242] text-xs font-bold uppercase tracking-[0.2em]"><GraduationCap size={16} /> Elite Coaching</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-3 tracking-tight">Coached by Sporting Academy professionals</h2>
          <p className="text-white/80 mt-4">Every session is delivered by genuine Sporting Academy professionals, bringing the methodology, standards, and soccer culture of one of the world's most respected youth academies directly to Arkansas.</p>
        </div>
      </div>
    </section>
  );
}

function ScheduleChip({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[#F2F8F5] border border-[#008057]/15 px-4 py-3">
      <div className="w-10 h-10 rounded-lg bg-white text-[#008057] flex items-center justify-center shrink-0"><Icon size={20} /></div>
      <div><div className="text-xs font-semibold uppercase tracking-widest text-[#008057]">{label}</div><div className="font-bold text-[#03281B]">{value}</div></div>
    </div>
  );
}

export function BenefitsSection({ schedule }) {
  return (
    <section id="overview" className={`${WRAP} py-16 md:py-24`}>
      <div className="text-xs font-semibold uppercase tracking-widest text-[#008057]">Standard Registration Includes</div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#03281B] mt-2 tracking-tight">Everything your player needs</h2>
      {schedule && (
        <div data-testid="camp-schedule" className="mt-6 flex flex-col sm:flex-row gap-4">
          <ScheduleChip Icon={Clock} label="Camp hours" value={schedule.camp_hours} />
          <ScheduleChip Icon={Dumbbell} label="Individual training" value={schedule.individual_training} />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
        {BENEFITS.map((b) => (
          <div key={b.title} className="rounded-2xl bg-white border border-[#E2EBF0] p-6 shadow-lg shadow-emerald-950/5 hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-[#F2F8F5] text-[#008057] flex items-center justify-center"><b.icon size={24} /></div>
            <h3 className="font-bold text-lg text-[#03281B] mt-4">{b.title}</h3>
            <p className="text-slate-600 text-sm mt-1">{b.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function JerseySection() {
  return (
    <>
      <section className="bg-[#03281B] brand-stripe">
        <div className={`${WRAP} py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center`}>
          <img src={JERSEY_IMG} alt="Retro Cristiano Ronaldo Sporting CP replica jersey (number 28)" className="w-full object-contain aspect-[4/3] drop-shadow-2xl" />
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F3C242]">First 5 Registrations Only</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-3 tracking-tight">Retro Cristiano Ronaldo Sporting CP Replica Jersey</h2>
            <p className="text-white/80 mt-4">The first five qualifying registrations receive a complimentary retro replica Cristiano Ronaldo Sporting CP jersey, commemorating his time at the club. Eligibility is confirmed by verified payment.</p>
            <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4 text-white/70 text-sm">One jersey per qualifying registration, awarded on a first-come, first-served basis and confirmed by verified payment.</div>
          </div>
        </div>
      </section>
      <div className="bg-[#03281B]"><div className={WRAP}><div className="h-0.5 bg-gradient-to-r from-transparent via-[#F3C242] to-transparent" /></div></div>
    </>
  );
}

const packageList = (packages) => packages.map((p, i, arr) => `${i === arr.length - 1 ? "or " : ""}${p.name.replace(" Sponsor", "")} ($${Number(p.price).toLocaleString("en-US")})`).join(", ");

export function SponsorSection({ packages, onSponsor }) {
  return (
    <section className="bg-[#03281B] brand-stripe">
      <div className={`${WRAP} py-16 md:py-20 text-center`}>
        <div className="inline-flex items-center gap-2 text-[#F3C242] text-xs font-bold uppercase tracking-[0.2em] justify-center"><Heart size={16} /> Child Sponsorship Program</div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-white mt-3 tracking-tight">SPONSOR A YOUNG LION</h2>
        <p data-testid="landing-sponsor-blurb" className="text-white/80 mt-4 max-w-2xl mx-auto">Help more children experience Sporting CP Camp Arkansas 2027. Choose a {packageList(packages)} sponsorship to fund 1, 2 or 5 full camp scholarships - and receive commemorative scarves and sponsor recognition, unless you choose to stay anonymous.</p>
        <button data-testid="sponsor-cta" onClick={onSponsor} className="mt-8 rounded-full bg-[#F3C242] hover:brightness-105 text-[#03281B] px-8 py-3.5 font-bold text-lg gold-glow">Sponsor a Young Lion</button>
      </div>
    </section>
  );
}

function PriceTier({ c }) {
  const active = c.status === "active";
  const badge = TIER_BADGE[c.status];
  return (
    <div data-testid={`price-tier-${c.key}`} className={`rounded-2xl p-6 border-2 transition-all ${active ? "border-[#008057] bg-[#F2F8F5] shadow-xl scale-[1.02]" : "border-[#E2EBF0] bg-white opacity-80"}`}>
      <div className="flex items-center justify-between">
        <span className="font-bold text-[#03281B]">{c.name}</span>
        {badge && <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${badge[0]}`}>{badge[1]}</span>}
      </div>
      <div className="font-display text-4xl font-extrabold text-[#03281B] mt-4">{usd(c.price)}</div>
      <div className="text-sm text-slate-500 mt-1">{c.valid}</div>
    </div>
  );
}

export function PricingSection({ categories, closed }) {
  return (
    <section id="pricing" className={`${WRAP} py-16 md:py-24`}>
      <div className="text-xs font-semibold uppercase tracking-widest text-[#008057]">Registration Pricing</div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#03281B] mt-2 tracking-tight">Three sequential price categories</h2>
      <p className="text-slate-600 mt-2">Only one category is active at a time. Prices are validated by the server.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
        {(categories || []).map((c) => <PriceTier key={c.key} c={c} />)}
      </div>
      {closed && <div className="mt-8 rounded-xl bg-red-50 border border-red-200 text-red-700 px-5 py-4 font-semibold">Registration for Sporting CP Camp Arkansas 2027 is now closed.</div>}
    </section>
  );
}

export function ServicesSection() {
  return (
    <section id="services" className="bg-[#F2F8F5]">
      <div className={`${WRAP} py-16 md:py-24`}>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#008057]">Optional Services</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#03281B] mt-2 tracking-tight">Elevate the camp experience</h2>
        <p className="text-slate-600 mt-2">All additional services are priced per participant and can be added during registration.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {SERVICES.map((s) => (
            <div key={s.name} data-testid={s.testid} className={`rounded-2xl p-6 border shadow-lg shadow-emerald-950/5 bg-white ${s.premium ? "border-[#F3C242] gold-glow" : "border-[#E2EBF0]"}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.premium ? "bg-[#F3C242] text-[#03281B]" : "bg-[#F2F8F5] text-[#008057]"}`}><s.icon size={24} /></div>
              <div className="flex items-center justify-between mt-4"><h3 className="font-bold text-[#03281B]">{s.name}</h3><span className="font-display font-extrabold text-[#008057]">{s.price}</span></div>
              <p className="text-slate-600 text-sm mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <img src={TRAIN_IMG} alt="Soccer training" className="rounded-2xl w-full object-cover aspect-video" />
          <img src={GK_IMG} alt="Goalkeeper training" className="rounded-2xl w-full object-cover aspect-video" />
        </div>
      </div>
    </section>
  );
}

export function FinalCtaSection({ closed, onRegister }) {
  return (
    <section className={`${WRAP} py-16 md:py-24 text-center`}>
      <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-[#03281B] tracking-tight">Ready to join the camp?</h2>
      <p className="text-slate-600 mt-3 max-w-xl mx-auto">Register your player in minutes. Secure checkout with credit card or bank transfer.</p>
      <button data-testid="final-register-cta" onClick={onRegister} disabled={closed} className="mt-8 rounded-full bg-[#008057] hover:bg-[#054D34] disabled:opacity-60 text-white px-8 py-3.5 font-bold text-lg transition-colors shadow-xl">
        {closed ? "Registration Closed" : "Start Registration"}
      </button>
      <p className="text-slate-400 text-sm mt-4 flex items-center justify-center gap-2"><CheckCircle2 size={16} /> Ages 6-17 &middot; June 28 – July 2, 2027 &middot; Brunner & Lay Soccer Complex</p>
    </section>
  );
}

export function ContactSection() {
  return (
    <section id="contact" className="bg-[#03281B] brand-stripe">
      <div className={`${WRAP} py-16 md:py-20 text-center`}>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#F3C242]">Get in touch</div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-2 tracking-tight">Questions about the camp?</h2>
        <p className="text-white/70 mt-3 max-w-xl mx-auto">Our team is happy to help. Reach out by email or message us directly on WhatsApp.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          <a data-testid="contact-email" href="mailto:arkansas@northcrestathletics.com" className="inline-flex items-center gap-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 px-6 py-3.5 text-white font-semibold transition-colors"><Mail size={20} /> arkansas@northcrestathletics.com</a>
          <a data-testid="contact-whatsapp" href="https://wa.me/16193010987" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 rounded-full bg-[#25D366] hover:brightness-105 px-6 py-3.5 text-[#03281B] font-bold transition-all shadow-xl"><MessageCircle size={20} /> Message us on WhatsApp</a>
        </div>
      </div>
    </section>
  );
}
